import { cldImg } from "../../../lib/cloudinary";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import Header from "../../../components/Header";
import { QRCodeCanvas } from "qrcode.react";
import confetti from "canvas-confetti";

/* 🆕 PÉRIODES — libellé de durée.
   Une réservation sur forfait porte periods et unitNights : on affiche
   "2 mois" plutôt que "60 nuits", qui est exact mais pas ce que le client
   a réservé. */
const UNIT_LABEL = { 7: "semaine", 30: "mois" };

function durationLabel(r) {
  if (r.periods && r.unitNights) {
    const l = UNIT_LABEL[r.unitNights] || "période";
    const plural = l === "mois" ? l : (r.periods > 1 ? `${l}s` : l);
    return `${r.periods} ${plural} · ${r.totalNights} nuits`;
  }
  if (!r.totalNights) return null;
  return `${r.totalNights} nuit${r.totalNights > 1 ? "s" : ""}`;
}

const fmtDateTime = (d) =>
  new Date(d).toLocaleString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
    hour: "2-digit", minute: "2-digit",
  });

function Confirmation() {
  const { id } = useParams();
  const [reservation, setReservation] = useState(null);

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        const res = await axios.get(
          `https://api.komorotel.com/api/reservation/${id}`,
          { withCredentials: true }
        );
        setReservation(res.data);
      } catch (err) {
        console.log(err);
      }
    };

    fetchReservation();
  }, [id]);

  // 🆕 VERSEMENT — une réservation en attente de règlement n'est pas confirmée.
  // Les dates sont bloquées, mais le séjour peut encore repartir à la vente.
  // Annoncer le contraire ferait manquer le paiement au client.
  const awaitingDeposit = reservation?.status === "awaiting_deposit";

  // 🎉 CONFETTI — uniquement quand il y a vraiment quelque chose à fêter
  useEffect(() => {
    if (!reservation || awaitingDeposit) return;

    confetti({
      particleCount: 120,
      spread: 80,
      origin: { y: 0.6 },
    });
  }, [reservation, awaitingDeposit]);

  if (!reservation) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-gray-500 animate-pulse">Chargement...</p>
      </div>
    );
  }

  const duration = durationLabel(reservation);
  const deposit  = reservation.deposit;

  return (
    <>
      <Header />

      <div className="pt-28 bg-gray-50 min-h-screen flex items-center justify-center px-4 pb-16">

        <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl border overflow-hidden animate-fadeIn">

          {/* 🖼️ IMAGE */}
          {reservation.roomPhoto && (
            <div className="h-52 w-full overflow-hidden">
              <img
                src={cldImg(reservation.roomPhoto, 800)}
                alt=""
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          )}

          <div className="p-8">

            {/* ✅ EN-TÊTE — le message suit le statut réel */}
            <div className="flex flex-col items-center text-center animate-slideUp">

              {awaitingDeposit ? (
                <>
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-100 mb-4">
                    <span className="text-amber-600 text-3xl">⏳</span>
                  </div>

                  <h1 className="text-2xl font-semibold text-gray-900">
                    Vos dates sont bloquées
                  </h1>

                  <p className="text-gray-500 mt-2 text-sm max-w-md">
                    La réservation sera confirmée dès que l'établissement aura
                    enregistré votre règlement.
                  </p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                    <span className="text-green-600 text-3xl">✓</span>
                  </div>

                  <h1 className="text-2xl font-semibold text-gray-900">
                    Réservation confirmée 🎉
                  </h1>

                  <p className="text-gray-500 mt-2 text-sm">
                    Votre séjour est validé. Profitez pleinement ✨
                  </p>
                </>
              )}

            </div>

            {/* 🆕 ORGANISATION — qui règle, et si le séjour est acquis.
                Un voyageur qui croit sa réservation confirmée alors qu'elle
                attend son responsable se présente devant un hébergeur qui
                ne l'attend pas. */}
            {reservation.organization && (
              <div
                className={`mt-8 rounded-xl p-5 border ${
                  reservation.organization.approvalStatus === "pending"
                    ? "border-amber-200 bg-amber-50"
                    : reservation.organization.approvalStatus === "rejected"
                    ? "border-red-200 bg-red-50"
                    : "border-blue-100 bg-blue-50"
                }`}
              >
                <p className="text-sm font-medium text-gray-900">
                  Pris en charge par {reservation.organization.name}
                </p>
                {reservation.organization.missionTitle && (
                  <p className="text-[13px] text-gray-600 mt-0.5">
                    Mission {reservation.organization.missionTitle}
                  </p>
                )}

                {reservation.organization.approvalStatus === "pending" && (
                  <p className="text-[13px] text-amber-900 mt-2.5 leading-relaxed">
                    Vos dates sont tenues, mais le séjour n'est pas encore acquis :
                    il attend la validation de votre responsable
                    {reservation.organization.approvalDueAt
                      ? `, au plus tard le ${fmtDateTime(reservation.organization.approvalDueAt)}`
                      : ""}.
                    Vous recevrez un message dès qu'il aura tranché.
                  </p>
                )}

                {reservation.organization.approvalStatus === "rejected" && (
                  <p className="text-[13px] text-red-700 mt-2.5 leading-relaxed">
                    Votre responsable n'a pas validé ce séjour, il est donc annulé.
                    {reservation.organization.rejectionReason
                      ? ` Motif indiqué : ${reservation.organization.rejectionReason}`
                      : ""}
                  </p>
                )}

                {["not_required", "approved"].includes(reservation.organization.approvalStatus) && (
                  <p className="text-[13px] text-gray-600 mt-2.5 leading-relaxed">
                    Le séjour est acquis et l'établissement est prévenu. Vous
                    n'avez rien à régler sur place.
                  </p>
                )}
              </div>
            )}

            {/* 🆕 VERSEMENT — ce qu'il reste à faire, mis en avant avant le
                récapitulatif : c'est l'information la plus utile de la page. */}
            {awaitingDeposit && deposit && (
              <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-5">

                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-sm text-amber-900 font-medium">
                    Montant à régler pour confirmer
                  </span>
                  <span className="text-2xl font-semibold text-amber-900 whitespace-nowrap">
                    {deposit.amount}€
                  </span>
                </div>

                {deposit.amountKMF && (
                  <div className="text-right text-xs text-amber-700 mt-1">
                    ≈ {deposit.amountKMF.toLocaleString("fr-FR")} KMF
                  </div>
                )}

                {deposit.dueAt && (
                  <div className="mt-4 pt-4 border-t border-amber-200 text-sm text-amber-900">
                    À régler avant le{" "}
                    <strong>{fmtDateTime(deposit.dueAt)}</strong>
                  </div>
                )}

                <p className="mt-3 text-xs text-amber-800 leading-relaxed">
                  Le règlement se fait directement auprès de {reservation.hotelName},
                  qui vous contacte par e-mail ou par téléphone. Rappelez la référence{" "}
                  <span className="font-mono font-medium">{reservation.reservationId}</span>.
                  Sans règlement à l'échéance, les dates repartent automatiquement à la vente.
                </p>

                {reservation.totalPrice > deposit.amount && (
                  <p className="mt-2 text-xs text-amber-700">
                    Solde de {Math.round((reservation.totalPrice - deposit.amount) * 100) / 100}€
                    à régler sur place.
                  </p>
                )}
              </div>
            )}

            {/* 📊 INFOS */}
            <div className="mt-8 border-t pt-6 space-y-4 animate-fadeIn delay-150">

              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Référence</span>
                <span className="font-medium">{reservation.reservationId}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Hôtel</span>
                <span className="font-medium">{reservation.hotelName}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Chambre</span>
                <span className="font-medium">{reservation.roomTitle}</span>
              </div>

              {reservation.ratePlan && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tarif</span>
                  <span className="font-medium">{reservation.ratePlan.name}</span>
                </div>
              )}


              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Dates</span>
                <span className="font-medium">
                  {new Date(reservation.checkInDate).toLocaleDateString()} →{" "}
                  {new Date(reservation.checkOutDate).toLocaleDateString()}
                </span>
              </div>

              {/* 🆕 PÉRIODES — durée dans l'unité réservée */}
              {duration && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Durée</span>
                  <span className="font-medium">{duration}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total</span>
                <span className="font-semibold text-gray-900">
                  {reservation.totalPrice}€
                </span>
              </div>

            </div>

            {/* 🔳 QR CODE — seulement une fois le séjour confirmé.
                Un QR présenté à l'arrivée sur une réservation non payée
                promettrait un accueil qui n'est pas encore acquis. */}
            {!awaitingDeposit && (
              <div className="mt-8 flex flex-col items-center animate-fadeIn delay-300">

                <p className="text-sm text-gray-500 mb-3">
                  Présentez ce QR code à votre arrivée
                </p>

                <div className="bg-white p-3 rounded-xl border shadow-sm">
                  <QRCodeCanvas
                    value={reservation.reservationId}
                    size={120}
                  />
                </div>

              </div>
            )}

            {/* 🔘 CTA */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 animate-fadeIn delay-500">

              <a
                href="/"
                className="flex-1 text-center px-5 py-2.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition"
              >
                Retour à l'accueil
              </a>

              <a
                href="/my-reservations"
                className="flex-1 text-center px-5 py-2.5 rounded-lg text-sm font-medium bg-[var(--k-green)] text-white hover:bg-[var(--k-green-hover)] transition"
              >
                Voir mes réservations
              </a>

            </div>

          </div>

        </div>

      </div>
    </>
  );
}

export default Confirmation;